/**
 * My Meetings' Items tab (D1): the open items across every virtual account, grouped by virtual
 * account and kind, each group a headed card (TMMAPP-0103). Moved here from the old page unchanged
 * in what it loads and who may change an item.
 *
 * A standalone meeting's items (TMMAPP-0159 §D 24, no virtual account) group by their meeting under
 * "With {names}", where a virtual account's group names the virtual account.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { meetingsApi, type MeetingItem } from '@/api/meetings';
import { virtualAccountsApi } from '@/api/virtualAccounts';
import type { Envelope } from '@/api/envelopes';
import { ErrorState } from '@/components/ErrorState';
import { HeadedCard } from '@/components/HeadedCard';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import { canWriteNotes } from './canWriteNotes';
import { resolveItemAddress, type ItemAddress } from './itemAddress';
import { ItemRow } from './ItemRow';
import { canWriteStandalone, meetingWithLabel } from './standalone';

interface PersonOption {
  userId: number;
  displayName: string;
}

interface ItemSurface {
  /** The group's heading: the virtual account's name, or a standalone meeting's people. */
  title: string | null;
  people: PersonOption[];
  currency: string | null;
  envelopes: Envelope[];
  /** The served `canManage` of the virtual account: its meeting writers (TMMAPP-0110). */
  canWrite: boolean;
}

const KINDS = ['DECISION', 'COMMITMENT', 'GOAL', 'QUESTION'] as const;

/** A virtual account's items group by it; a standalone meeting's by the meeting. */
const groupKey = (item: MeetingItem) =>
  item.virtualAccountId === null ? `m${item.meetingId}` : `v${item.virtualAccountId}`;

interface MeetingsItemsTabProps {
  /** The page's one items refresh (TMMAPP-0154 item 18): a change reloads the tab. */
  reloadToken?: number;
  /** A change here: the page refreshes the tab and the overview cards together. */
  onChanged?: () => void;
}

export function MeetingsItemsTab({ reloadToken = 0, onChanged }: MeetingsItemsTabProps = {}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const tokens = resolveTokens(useTheme());
  const [openItems, setOpenItems] = useState<MeetingItem[] | null>(null);
  const [addresses, setAddresses] = useState<Map<number, ItemAddress | null>>(new Map());
  const [surfaces, setSurfaces] = useState<Map<string, ItemSurface>>(new Map());
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const started = useRef(false);

  const load = useCallback(async () => {
    try {
      const items = await meetingsApi.listMyItems('OPEN');
      const groups = new Map<string, MeetingItem[]>();
      for (const item of items) {
        const list = groups.get(groupKey(item)) ?? [];
        list.push(item);
        groups.set(groupKey(item), list);
      }
      const next = new Map<number, ItemAddress | null>();
      for (const item of items) next.set(item.id, resolveItemAddress(item));
      const nextSurfaces = new Map<string, ItemSurface>();
      await Promise.all(
        [...groups.entries()].map(async ([key, rows]) => {
          const first = rows[0]!;
          const virtualAccountId = first.virtualAccountId;
          if (virtualAccountId === null) {
            // A standalone meeting: its people come from the meeting itself (talk only, no money).
            const meeting = await meetingsApi.get(null, first.meetingId).catch(() => null);
            const people =
              meeting === null
                ? []
                : [
                    meeting.organizer,
                    ...meeting.invitees.filter((invite) => invite.status !== 'CANCELLED'),
                  ].flatMap((person) =>
                    person.userId === null
                      ? []
                      : [{ userId: person.userId, displayName: personName(person.displayName, t) }],
                  );
            const organizer =
              meeting !== null && me.user.id !== null && meeting.organizer.userId === me.user.id;
            nextSurfaces.set(key, {
              title: meeting === null ? null : meetingWithLabel(meeting, me.user.id, t, locale),
              people,
              currency: null,
              envelopes: [],
              canWrite: canWriteStandalone(me, organizer),
            });
            return;
          }
          const virtualAccountDetail = await virtualAccountsApi
            .get(virtualAccountId)
            .catch(() => null);
          const meetingIds = new Set(rows.map((row) => row.meetingId));
          const people: PersonOption[] = [];
          await Promise.all(
            [...meetingIds].map(async (meetingId) => {
              const meeting = await meetingsApi.get(virtualAccountId, meetingId).catch(() => null);
              if (meeting === null || meeting.organizer.userId === null) return;
              people.push({
                userId: meeting.organizer.userId,
                displayName: meeting.organizer.displayName,
              });
            }),
          );
          if (virtualAccountDetail !== null) {
            for (const share of virtualAccountDetail.shares) {
              if (share.status !== 'ACTIVE' || share.userId === null) continue;
              if (people.some((person) => person.userId === share.userId)) continue;
              people.push({
                userId: share.userId,
                displayName: personName(share.displayName, t),
              });
            }
          }
          nextSurfaces.set(key, {
            title:
              me.virtualAccounts.find((row) => row.id === virtualAccountId)?.name ??
              String(virtualAccountId),
            people,
            currency:
              virtualAccountDetail?.currency ??
              me.virtualAccounts.find((row) => row.id === virtualAccountId)?.currency ??
              null,
            envelopes: virtualAccountDetail?.envelopes ?? [],
            canWrite: canWriteNotes(virtualAccountDetail),
          });
        }),
      );
      setOpenItems(items);
      setAddresses(next);
      setSurfaces(nextSurfaces);
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, [me, t, locale]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  const seenToken = useRef(reloadToken);
  useEffect(() => {
    if (seenToken.current === reloadToken) return;
    seenToken.current = reloadToken;
    void load();
  }, [reloadToken, load]);

  if (loadFailure !== null) {
    return (
      <ErrorState
        error={loadFailure.error}
        what={t('errors.what.meetings')}
        onRetry={() => void load()}
      />
    );
  }
  if (openItems === null) return <Typography>{t('common.loading')}</Typography>;
  if (openItems.length === 0) {
    return (
      <HeadedCard title={t('workspace.items')}>
        <Typography sx={{ color: tokens.text2 }}>{t('items.openEmpty')}</Typography>
      </HeadedCard>
    );
  }

  const groups = new Map<string, MeetingItem[]>();
  for (const item of openItems) {
    const list = groups.get(groupKey(item)) ?? [];
    list.push(item);
    groups.set(groupKey(item), list);
  }

  return (
    <Stack sx={{ gap: 2 }}>
      {[...groups.entries()].map(([key, rows]) => {
        const surface = surfaces.get(key);
        return (
          <HeadedCard key={key} title={surface?.title ?? t('meetings.justUs')}>
            {KINDS.map((kind) => {
              const ofKind = rows.filter((row) => row.kind === kind);
              if (ofKind.length === 0) return null;
              return (
                <Box key={kind} sx={{ '&:not(:first-of-type)': { mt: 2 } }}>
                  <Typography variant="kindLabel" component="h3">
                    {t(`items.kind.${kind}`)}
                  </Typography>
                  {ofKind.map((item) => {
                    const address = addresses.get(item.id) ?? null;
                    const canWrite = surface?.canWrite === true;
                    return (
                      <ItemRow
                        key={item.id}
                        item={item}
                        address={address}
                        canWrite={canWrite}
                        people={surface?.people ?? []}
                        {...(surface?.currency !== undefined ? { currency: surface.currency } : {})}
                        envelopes={surface?.envelopes ?? []}
                        onChanged={() => {
                          if (onChanged !== undefined) onChanged();
                          else void load();
                        }}
                      />
                    );
                  })}
                </Box>
              );
            })}
          </HeadedCard>
        );
      })}
    </Stack>
  );
}
